import type { ImportPreview } from '@/api/planDetail'
import type { ProductListItemRead, ValidationError } from '@/api/productLists'
import type { HierarchyCorrectionMapping } from '@/hooks/useProductLists'

/** One problem in the file, grouped by column + value so a single fix in the
 * spreadsheet resolves every row listed. */
export interface ImportIssueEntry {
  id: string
  column: string
  value: string | null
  correctedValue?: string | null
  /** What's wrong, in the user's words. */
  message: string
  /** How to fix it, or what the import does with the value. */
  hint?: string
  rows: number[]
}

type PreviewIssue = ImportPreview['issues'][number]

class EntryCollector {
  private entries = new Map<
    string,
    ImportIssueEntry & { rowSet: Set<number> }
  >()

  add(entry: Omit<ImportIssueEntry, 'id' | 'rows'>, row: number) {
    const id = JSON.stringify([
      entry.column,
      entry.value,
      entry.correctedValue ?? null,
      entry.message,
    ])
    const existing = this.entries.get(id)
    if (existing) {
      existing.rowSet.add(row)
    } else {
      this.entries.set(id, { ...entry, id, rows: [], rowSet: new Set([row]) })
    }
  }

  toArray(): ImportIssueEntry[] {
    return Array.from(this.entries.values(), ({ rowSet, ...entry }) => ({
      ...entry,
      rows: [...rowSet].sort((a, b) => a - b),
    }))
  }
}

const issueKey = (row: number, column: string, message: string) =>
  JSON.stringify([row, column, message])

function describeMismatch(err: ValidationError) {
  if (err.code === 'DROPDOWN_VALUE_NOT_FOUND') {
    return {
      message: `Isn't a ${err.column} option`,
      hint: `Use one of the ${err.column} values from the template.`,
    }
  }
  return {
    message: err.message,
    hint: 'Check that Department, Class and Subclass belong to the same branch of the hierarchy.',
  }
}

/** The backend already explains the rule in plain words; add how to get
 * past it in an import. */
function describeEditability(message: string) {
  return {
    message,
    hint: 'Keep the current value from the plan for this row.',
  }
}

function describeWarning(err: ValidationError) {
  switch (err.code) {
    case 'ROUNDED_INTEGER':
      return { message: err.message }
    case 'IMAGE_NOT_FOUND':
      return {
        message: err.message,
        hint: 'The item is imported without this image.',
      }
    case 'DUPLICATE_UNIQUE_ID':
      return {
        message: err.message,
        hint: 'The first row with this Unique ID updates the original line.',
      }
    default:
      return {
        message: err.message,
        hint: 'This value is imported as blank. Fix it in your file to keep a value.',
      }
  }
}

/** Drop value mismatches the import preview found unchanged: the row keeps
 * the item's current value (e.g. a re-imported export), so it isn't an edit. */
export function withoutUnchangedMismatches(
  mismatchItems: ProductListItemRead[],
  unchanged: PreviewIssue[] | undefined
): ProductListItemRead[] {
  if (!unchanged?.length) return mismatchItems
  const skipped = new Set(
    unchanged.map(issue =>
      issueKey(issue.row_number, issue.column, issue.message)
    )
  )
  return mismatchItems.flatMap(item => {
    const errors = (item.errors ?? []).filter(
      err => !skipped.has(issueKey(item.row_number, err.column, err.message))
    )
    return errors.length > 0 ? [{ ...item, errors }] : []
  })
}

/** Problems that block the whole import: values that don't match an option
 * (from the value scan) plus edits the plan doesn't allow (from the import
 * preview). The preview repeats the mismatch errors, so those are deduped. */
export function buildBlockingEntries(
  mismatchItems: ProductListItemRead[],
  previewIssues: PreviewIssue[]
): ImportIssueEntry[] {
  const collector = new EntryCollector()
  const seen = new Set<string>()
  for (const item of mismatchItems) {
    for (const err of item.errors ?? []) {
      seen.add(issueKey(item.row_number, err.column, err.message))
      collector.add(
        { column: err.column, value: err.value, ...describeMismatch(err) },
        item.row_number
      )
    }
  }
  for (const issue of previewIssues) {
    if (seen.has(issueKey(issue.row_number, issue.column, issue.message)))
      continue
    collector.add(
      {
        column: issue.column,
        value: null,
        ...describeEditability(issue.message),
      },
      issue.row_number
    )
  }
  return collector.toArray()
}

/** Values that import, but not as written (blank, rounded, image not linked). */
export function buildWarningEntries(
  warningItems: ProductListItemRead[]
): ImportIssueEntry[] {
  const collector = new EntryCollector()
  for (const item of warningItems) {
    for (const err of item.errors ?? []) {
      collector.add(
        { column: err.column, value: err.value, ...describeWarning(err) },
        item.row_number
      )
    }
  }
  return collector.toArray()
}

export function buildCorrectionEntries(
  mappings: HierarchyCorrectionMapping[]
): ImportIssueEntry[] {
  return mappings.map(({ id, correction, rowNumbers }) => ({
    id,
    column: correction.column,
    value: correction.value,
    correctedValue: correction.corrected_value,
    message: correction.message,
    rows: rowNumbers,
  }))
}

/** Number of issues per file row, for rows that have any. */
export function issueCountByRow(entries: ImportIssueEntry[]) {
  const counts = new Map<number, number>()
  for (const entry of entries) {
    for (const row of entry.rows) counts.set(row, (counts.get(row) ?? 0) + 1)
  }
  return counts
}

export function pluralize(count: number, singular: string, plural?: string) {
  return `${count.toLocaleString()} ${count === 1 ? singular : (plural ?? `${singular}s`)}`
}

interface ReportColumn {
  header: string
  key: string
  width: number
}

/** Builds an Excel workbook with a frozen, filterable header and downloads it. */
async function downloadWorkbook(
  fileName: string,
  sheetName: string,
  columns: ReportColumn[],
  rows: Record<string, string | number>[]
) {
  // Loaded on demand so the workbook library stays out of the main bundle.
  const { default: ExcelJS } = await import('exceljs')
  const workbook = new ExcelJS.Workbook()
  const sheet = workbook.addWorksheet(sheetName, {
    views: [{ state: 'frozen', ySplit: 1 }],
  })
  sheet.columns = columns
  sheet.addRows(rows)
  const header = sheet.getRow(1)
  header.font = { bold: true }
  header.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFF8F8F8' },
  }
  sheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: columns.length },
  }
  sheet.eachRow(row => {
    row.alignment = { vertical: 'top', wrapText: true }
  })

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  URL.revokeObjectURL(url)
}

const baseName = (fileName: string) => fileName.replace(/\.[^.]+$/, '')

/** One line per affected row, so the user can work through the fixes next to
 * their spreadsheet. */
export function downloadIssueReport(
  fileName: string,
  entries: ImportIssueEntry[]
) {
  return downloadWorkbook(
    `${baseName(fileName)}-issues.xlsx`,
    'Issues',
    [
      { header: 'Row', key: 'row', width: 8 },
      { header: 'Column', key: 'column', width: 24 },
      { header: 'Value in file', key: 'value', width: 28 },
      { header: 'Issue', key: 'issue', width: 60 },
      { header: 'How to fix', key: 'fix', width: 60 },
    ],
    entries
      .flatMap(entry => entry.rows.map(row => ({ row, entry })))
      .sort((a, b) => a.row - b.row)
      .map(({ row, entry }) => ({
        row,
        column: entry.column,
        value: entry.value ?? '',
        issue: entry.message,
        fix: entry.hint ?? '',
      }))
  )
}

/** One line per error on the rows that won't be imported. */
export function downloadRejectedRowsReport(
  fileName: string,
  items: ProductListItemRead[]
) {
  return downloadWorkbook(
    `${baseName(fileName)}-skipped-rows.xlsx`,
    'Skipped rows',
    [
      { header: 'Row', key: 'row', width: 8 },
      { header: 'SKU #', key: 'sku', width: 16 },
      { header: 'Description', key: 'description', width: 32 },
      { header: 'Column', key: 'column', width: 24 },
      { header: 'Value in file', key: 'value', width: 28 },
      { header: 'Issue', key: 'issue', width: 60 },
    ],
    [...items]
      .sort((a, b) => a.row_number - b.row_number)
      .flatMap(item =>
        (item.errors ?? []).map(err => ({
          row: item.row_number,
          sku: item.parsed_data?.plan_sku_num ?? '',
          description: item.parsed_data?.description ?? '',
          column: err.column,
          value: err.value ?? '',
          issue: err.message,
        }))
      )
  )
}
